import styled from '@emotion/styled';
import * as HomeyApi from 'homey-api';
import { useState } from 'react';

import { athomCloudApi } from '../lib/athomCloudApi';
import { wait } from '../lib/wait';
import { ToastManager } from '../ToastManager';

import { useAuth } from '../store/AuthStore';

import { useQuery } from '../hooks/useQuery';

import { su } from '../theme/functions/su';
import { theme } from '../theme/theme';

import { GradientButton } from '../components/buttons/GradientButton';
import { ImageBase } from '../components/common/ImageBase';
import { SpinnerFade } from '../components/common/SpinnerFade';
import { Heading3Medium } from '../components/common/text/Heading3Medium';
import { ParagraphRegular } from '../components/common/text/ParagraphRegular';
import { MainNav } from '../components/main/nav/MainNav';

export function InvitePage(props) {
  const { cloudUser } = useAuth();

  const [isAccepting, setIsAccepting] = useState(false);

  const params = useQuery();
  const inviteToken = params.get('inviteToken');
  const homeyId = params.get('homeyId');
  const ownerId = params.get('ownerId');
  const ownerName = params.get('ownerName');

  return (
    <InvitePage.Root>
      <MainNav isMinimal={true} />

      <InvitePage.Content>
        <InvitePage.Centered>
          <ImageBase
            url={`https://avatars.athom.com/large/${ownerId}.png`}
            width={88}
            height={88}
            round={true}
          />

          <InvitePage.Title>Accept Invite?</InvitePage.Title>
          <InvitePage.Description color={theme.color.text_light}>
            {ownerName} invited you to their Homey. Would you like to get access?
          </InvitePage.Description>

          <GradientButton
            styleWidth="full"
            isDisabled={isAccepting === true}
            onPress={() => {
              setIsAccepting(true);
              const connectApi = new HomeyApi.AthomConnectAPI({
                baseUrl:
                  localStorage.getItem('ATHOM_CONNECT_API_BASEURL') ??
                  import.meta.env.VITE_ATHOM_CONNECT_API_BASEURL,
              });
              const min = wait(500);

              // A signed token bound to this Homey proves to Connect which Athom user accepts.
              // If it cannot be created, the accept is still sent without it, as before.
              athomCloudApi
                .createDelegationToken({ audience: 'connect-invite', homeyId })
                .catch((error) => {
                  console.error('Failed to create invite accept token', error);
                  return undefined;
                })
                .then((token) => {
                  return connectApi.acceptSharingRequest({
                    athomId: cloudUser._id,
                    homeyId: homeyId,
                    inviteToken: inviteToken,
                    token: token,
                  });
                })
                .then(async (response) => {
                  await min;
                  // setIsAccepting(false);
                  window.location.assign(`/homeys/${homeyId}`);
                })
                .catch(async (error) => {
                  await min;
                  setIsAccepting(false);
                  ToastManager.handleError(error);
                });
            }}
          >
            <SpinnerFade
              isActive={isAccepting === true}
              color={theme.color.white}
              size={theme.icon.size_small}
            />
            <GradientButton.Text>Accept</GradientButton.Text>
          </GradientButton>
        </InvitePage.Centered>
      </InvitePage.Content>
    </InvitePage.Root>
  );
}

InvitePage.Root = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
`;

InvitePage.Content = styled.main`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
`;

InvitePage.Centered = styled.div`
  width: 320px;
  text-align: center;
`;

InvitePage.Title = styled(Heading3Medium)`
  padding-top: ${su(2)};
`;

InvitePage.Description = styled(ParagraphRegular)`
  margin-bottom: ${su(5)};
`;
